/**
 * L1 — portfolio pillar registry (weights + source report mapping).
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { RiskCategory } from '../../types/siteRag';

export type PillarId =
  | 'power' | 'interconnection' | 'water' | 'land' | 'environmental'
  | 'regulatory' | 'supplyChain' | 'financial' | 'social' | 'evidenceQuality';

export interface PillarDefinition {
  label: string;
  weight: number;
  sourceReports?: readonly ReportTypeId[];
  structuredSource?: 'financialAssumptions' | 'auditFindings';
  /**
   * CONNECTIVITY-ADVISORY (2026-08-25) — an advisory pillar is INFORMATIONAL:
   * it is still scored and rendered (Deep Dive, portfolio breakdowns), but it
   * is excluded from the benefit-composite aggregate, from the unmeasured-
   * pillar confidence charge, and from the adequacy coverage ratio. It can
   * neither raise nor lower the GO/NO-GO band, and its absence costs nothing.
   *
   * The flag exists because the underlying data does not yet support gating:
   * see `CONNECTIVITY_GATES_GO` in `src/lib/pathToGo/mandatoryReports.ts` —
   * the requirement half of the same demotion. The two halves MUST move
   * together: a pillar in the composite whose report is not in the required
   * set sits in a dead zone (can raise the band, never lower it), which is
   * the exact defect the 2026-08-09 accuracy pass removed.
   *
   * The weight is RETAINED (not renormalized away) so `pillarWeightSum()`
   * stays 1.0, the `PillarId` union in persisted composites is untouched,
   * and re-promotion is a flag flip rather than a recalibration.
   */
  advisory?: boolean;
}

export const PILLARS: Record<PillarId, PillarDefinition> = {
  // Connectivity has its own visible advisory pillar. Feeding it into Power as
  // well would let an explicitly optional study alter the decision through a
  // non-advisory back door.
  // STAGE 5.2 — fed by BOTH, matching the established pattern for `financial`
  // (['financial','feasibility']) and `land` (['test-fit','feasibility']).
  //
  // 5.1 moved the grid-SCREENING half into BTM Feasibility, so feasibility now
  // carries real, provider-grounded power evidence and must be able to measure
  // this pillar. Without this, gating Generation & Impact on a promoted scenario
  // would leave every ordinary site with the 0.21-weight power pillar UNMEASURED
  // — and an unmeasured pillar is dropped from both sides of the weighted mean,
  // which is the exact composite-raising defect this whole workstream exists to
  // remove. A promoted scenario still sharpens the pillar; it no longer decides
  // whether power is measured at all.
  //
  // The label is the DISPLAY name only; the report id stays 'power-availability'
  // (see 5.5 — no ReportTypeId has ever been renamed, and doing so would break
  // typedPreferencesAtGen currency for five unrelated report types).
  power: { label: 'Generation & Impact', weight: 0.21, sourceReports: ['power-availability', 'feasibility'] },
  // `advisory: true` — connectivity data coverage (single provider, five
  // fields, no carrier/latency evidence) cannot yet support a gating pillar.
  // See the PillarDefinition.advisory doc + CONNECTIVITY_GATES_GO.
  interconnection: { label: 'Interconnection', weight: 0.10, sourceReports: ['interconnection'], advisory: true },
  // Phase 3 part B (2026-09-07): the standalone Water Resource report is
  // RETIRED. The water SCREEN (rights, stress, drought, supply) is a chapter of
  // the feasibility report; the scenario's water DUTY rides on Generation &
  // Impact (P3.2). Existing water reports still feed this pillar while they are
  // the latest current source of a `water` finding, so a retired report never
  // silently drops out of a persisted composite.
  water: { label: 'Water Resource', weight: 0.10, sourceReports: ['feasibility', 'power-availability', 'water-resource'] },
  land: { label: 'Land / Constructability', weight: 0.05, sourceReports: ['test-fit', 'feasibility'] },
  environmental: { label: 'Environmental', weight: 0.08, sourceReports: ['environmental'] },
  regulatory: { label: 'Regulatory & Political', weight: 0.10, sourceReports: ['feasibility', 'environmental'] },
  supplyChain: { label: 'Supply Chain', weight: 0.08, sourceReports: ['supply-chain-risk'] },
  // `'financial'` FIRST: the Financial Viability Report is the dedicated source
  // for this pillar and was previously absent, so a red financial report floored
  // the Go/No-Go rollup (PATH 2) while contributing NOTHING to the composite
  // that produces the base band. `'feasibility'` is retained as the fallback
  // basis — it carries `financialAssumptions`, which is a legitimate financial
  // signal — so adding the dedicated source is purely ADDITIVE and never turns
  // a previously-measured pillar `missing`.
  financial: { label: 'Financial', weight: 0.15, sourceReports: ['financial', 'feasibility'], structuredSource: 'financialAssumptions' },
  social: { label: 'Public Sentiment', weight: 0.10, sourceReports: ['public-sentiment'] },
  evidenceQuality: { label: 'Evidence Quality', weight: 0.03, structuredSource: 'auditFindings' },
} as const;

export const PILLAR_IDS = Object.keys(PILLARS) as PillarId[];

/**
 * R4a — which pillar a structured `RiskItem.category` scores against.
 *
 * A finding must land on ONE pillar, not all ten: applying a regulatory risk to
 * the water pillar would double-count it across the composite and let a single
 * finding move every axis at once. `RiskCategory` is a closed union
 * (`src/types/siteRag.ts`), so this map is total and the compiler rejects a
 * member without a home.
 *
 *   - regulatory     → permitting / political exposure.
 *   - environmental  → ecological / emissions exposure.
 *   - infrastructure → the physical power path (grid, interconnect, on-site
 *                      generation) — the `power` pillar carries it.
 *   - execution      → schedule and delivery exposure, which is what the
 *                      supplyChain pillar measures (lead times, COD risk).
 *   - water          → rights, stress, curtailment, withdrawal permits — the
 *                      `water` pillar (P1.5, 2026-09-05). Before this member
 *                      existed a water finding had to borrow `environmental`
 *                      or `regulatory`, and the water pillar could read 5.0
 *                      beside a disqualifying water problem on any report
 *                      that feeds more than one pillar.
 *
 * A category reaches its pillar only through a report that FEEDS that pillar
 * (`computeSiteComposite` scores each pillar from `reportsForPillar`). Since
 * Phase 3 part B (2026-09-07) feasibility FEEDS `water` (it carries the water
 * screen), so a `water` row on feasibility now scores the water pillar;
 * `environmental` and `execution` rows on feasibility stay inert. Cross-report
 * routing is a separate design.
 */
export const RISK_CATEGORY_TO_PILLAR: Readonly<Record<RiskCategory, PillarId>> = {
  regulatory: 'regulatory',
  environmental: 'environmental',
  infrastructure: 'power',
  execution: 'supplyChain',
  water: 'water',
};

/**
 * The decision pillars a report type feeds — the inverse of `sourceReports`.
 */
export function pillarsForReportType(type: ReportTypeId): PillarId[] {
  return PILLAR_IDS.filter((id) => (PILLARS[id].sourceReports ?? []).includes(type));
}

/**
 * True when EVERY decision pillar this report type feeds is advisory — i.e.
 * the report is informational and may not move the GO/NO-GO band.
 *
 * Derived from the same `advisory` flag the composite aggregate consults, so
 * the two cannot drift: a pillar excluded from the aggregate is excluded from
 * band authority too. A type that feeds NO pillar is deliberately not advisory
 * — it simply has no composite influence, which is a different thing from
 * being designated informational.
 */
export function isAdvisoryReportType(type: ReportTypeId): boolean {
  const pillars = pillarsForReportType(type);
  return pillars.length > 0 && pillars.every(isAdvisoryPillar);
}

/**
 * The one pillar a report type feeds, or null when it feeds several.
 *
 * Used to route a report's STRUCTURED risk rows to the pillar they concern.
 * `RISK_CATEGORY_TO_PILLAR` was a four-value enum covering regulatory /
 * environmental / infrastructure / execution, so `water`, `land`, `financial`
 * and `social` could never receive a finding-severity penalty at all: a
 * critical risk reading "no water rights, cannot be permitted" was emitted as
 * `environmental` and charged to the 0.08-weight environmental pillar instead
 * of the 0.10-weight water pillar it describes.
 *
 * A risk on a single-pillar report is about that pillar by construction, so it
 * reaches that pillar whatever category it was emitted under. Multi-pillar
 * reports (feasibility → power/land/regulatory/financial, environmental →
 * environmental/regulatory) still need the category to discriminate, which is
 * exactly what it is good at — and since P1.5 the category can also say
 * `water` outright (`RiskCategory`).
 */
export function soleDecisionPillarForReportType(type: ReportTypeId): PillarId | null {
  const pillars = pillarsForReportType(type);
  return pillars.length === 1 ? pillars[0] ?? null : null;
}

export function pillarWeightSum(): number {
  return PILLAR_IDS.reduce((sum, id) => sum + PILLARS[id].weight, 0);
}

/**
 * True when `pillarId` is advisory — scored + displayed but excluded from the
 * composite aggregate, the unmeasured-weight confidence charge, the adequacy
 * coverage ratio, and every pillar-derived blocker/drag ranking. The ONE
 * predicate every aggregate reimplementation must consult (see
 * `PillarDefinition.advisory`).
 */
export function isAdvisoryPillar(pillarId: PillarId): boolean {
  // Optional-chained: persisted composites and test fixtures can carry pillar
  // ids outside the current registry; an unknown pillar is NOT advisory
  // (fail-closed — it keeps whatever treatment the caller's loop gives it).
  return PILLARS[pillarId]?.advisory === true;
}

/**
 * Site-aware decision-material predicate. Persisted composites created before
 * the applicability field remain applicable unless their static registry
 * definition is advisory.
 */
export function isDecisionApplicablePillar(
  pillarId: PillarId,
  score?: { applicability?: 'applicable' | 'not-applicable' } | null,
): boolean {
  return !isAdvisoryPillar(pillarId)
    && score?.applicability !== 'not-applicable';
}

/**
 * The label for a pillar that is NOT scored on this site — the shared half of
 * the applicability tri-state (`Applicable` / `Optional` / `Available later`).
 *
 * Was inlined at five surfaces (CSV export, DOCX table, Deep Dive, risk matrix,
 * data-confidence drawer), which is how the advisory test drifted: four sites
 * called `isAdvisoryPillar()` and one reached for `PILLARS[id].advisory`
 * directly, bypassing the optional-chained guard that exists because persisted
 * composites can carry pillar ids outside the current registry.
 *
 * On screen only the advisory "Optional" is shown: the "Available later" tag
 * and its compact "Later" form were removed from the Deep Dive, risk matrix
 * and confidence drawer (owner, 2026-09-30). "Available later" remains the
 * CSV / DOCX export wording.
 *
 * The `'Applicable'` case is deliberately NOT here — only the CSV export emits
 * that string; every other surface renders a score in that branch.
 */
export function inapplicablePillarLabel(pillarId: PillarId): string {
  if (isAdvisoryPillar(pillarId)) return 'Optional';
  return 'Available later';
}

/** Registry-derived denominator for every decision-coverage explanation. */
export const DECISION_PILLAR_COUNT = PILLAR_IDS.filter(
  (pillarId) => !isAdvisoryPillar(pillarId),
).length;

/**
 * Registry-derived denominator for decision-WEIGHT coverage.
 *
 * `DECISION_PILLAR_COUNT` treats every pillar as interchangeable, but they are
 * not: `power` carries 0.21 of the decision and `land` 0.05, so a site can
 * measure a MAJORITY of pillars while measuring a MINORITY of the decision.
 * Advisory pillars are excluded from both sides here for the same reason they
 * are excluded from the count frame — a site must not be able to buy coverage
 * with an informational pillar.
 */
export const DECISION_PILLAR_WEIGHT_SUM = PILLAR_IDS.filter(
  (pillarId) => !isAdvisoryPillar(pillarId),
).reduce((sum, pillarId) => sum + PILLARS[pillarId].weight, 0);

export function reportsForPillar(pillarId: PillarId): ReportTypeId[] {
  return [...(PILLARS[pillarId].sourceReports ?? [])];
}
